import { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listExerciseModes } from "@/api/catalog";
import { listFunctionalRoles } from "@/api/functionalRoles";
import { realizeAssignment, updateAssignment } from "@/api/planning";
import { listPractitioners } from "@/api/practitioners";
import type {
  Assignment,
  ExerciseMode,
  Practitioner,
  RealizedStatus,
  SlotTemplate,
} from "@/api/types";

type Props = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  year: number;
  month: number;
  /** The shift being corrected. A block spanning several slots edits the first. */
  assignment: Assignment | null;
  slots: SlotTemplate[];
};

const REALIZED_STATUSES: RealizedStatus[] = [
  "PLANNED",
  "DONE",
  "MODIFIED",
  "ABSENT",
  "EXTRA",
];

/** `2026-07-22T19:30:00Z` → `2026-07-22T21:30`, the value a datetime-local wants. */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Correct one shift after the fact: move it, hand it to someone else, change
 * the exercise mode, and record what actually happened. The forecast it
 * departs from is snapshotted server-side on the first change, so the two
 * sides stay comparable on the payroll sheet.
 */
export function AssignmentEditDialog({
  open,
  onClose,
  organizationId,
  year,
  month,
  assignment,
  slots,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [date, setDate] = useState("");
  const [slotId, setSlotId] = useState("");
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [exerciseModeId, setExerciseModeId] = useState("");
  const [functionalRoleId, setFunctionalRoleId] = useState("");
  const [comment, setComment] = useState("");
  const [realizedStatus, setRealizedStatus] = useState<RealizedStatus>("PLANNED");
  const [actualStart, setActualStart] = useState("");
  const [actualEnd, setActualEnd] = useState("");
  const [realizedMinutes, setRealizedMinutes] = useState("");
  const [managerNote, setManagerNote] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const practitioners = useQuery({
    queryKey: ["practitioners-active-300"],
    queryFn: () => listPractitioners({ status: "ACTIVE", limit: 200 }),
    enabled: open,
  });
  const modes = useQuery({
    queryKey: ["exercise-modes-active"],
    queryFn: () => listExerciseModes(true),
    enabled: open,
  });
  const roles = useQuery({
    queryKey: ["functional-roles-active", organizationId],
    queryFn: () => listFunctionalRoles({ isActive: true, organizationId }),
    enabled: open,
  });

  useEffect(() => {
    if (!open || !assignment) return;
    setDate(assignment.date);
    setSlotId(assignment.slot_template_id);
    setExerciseModeId(assignment.exercise_mode_id);
    setFunctionalRoleId(assignment.functional_role_id ?? "");
    setComment(assignment.comment ?? "");
    setRealizedStatus(assignment.realized_status);
    setActualStart(toLocalInput(assignment.actual_start_at));
    setActualEnd(toLocalInput(assignment.actual_end_at));
    setRealizedMinutes(
      assignment.realized_minutes === null ? "" : String(assignment.realized_minutes),
    );
    setManagerNote(assignment.manager_note ?? "");
    setServerError(null);
  }, [open, assignment]);

  // The practitioner list arrives after the dialog opens, so bind the current
  // holder once it is available rather than in the reset effect above.
  useEffect(() => {
    if (!assignment || !practitioners.data) return;
    setPractitioner(
      practitioners.data.items.find((p) => p.id === assignment.practitioner_id) ?? null,
    );
  }, [assignment, practitioners.data]);

  const sortedSlots = useMemo(
    () => [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [slots],
  );
  const modeById = useMemo(
    () => new Map((modes.data ?? []).map((m: ExerciseMode) => [m.id, m])),
    [modes.data],
  );

  const daysInMonth = new Date(year, month, 0).getDate();
  const monthDates = useMemo(
    () =>
      Array.from(
        { length: daysInMonth },
        (_, i) => `${year}-${String(month).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`,
      ),
    [year, month, daysInMonth],
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (!assignment) throw new Error("no_assignment");
      // Two calls on purpose: the structural edit and the réalisé declaration
      // are separate endpoints with separate audit entries.
      await updateAssignment(organizationId, year, month, assignment.id, {
        date,
        slot_template_id: slotId,
        practitioner_id: practitioner?.id ?? assignment.practitioner_id,
        exercise_mode_id: exerciseModeId,
        functional_role_id: functionalRoleId || null,
        comment: comment || null,
      });
      await realizeAssignment(organizationId, year, month, assignment.id, {
        realized_status: realizedStatus,
        actual_start_at: fromLocalInput(actualStart),
        actual_end_at: fromLocalInput(actualEnd),
        realized_minutes: realizedMinutes === "" ? null : Number(realizedMinutes),
        manager_note: managerNote || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planning", organizationId, year, month] });
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      onClose();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data
        ?.detail;
      setServerError(
        typeof detail === "string"
          ? t(`timesheets.error.${detail}`, { defaultValue: detail })
          : (e as Error).message,
      );
    },
  });

  const plannedChanged =
    assignment !== null &&
    assignment.planned_date !== null &&
    (assignment.planned_date !== date ||
      assignment.planned_slot_template_id !== slotId ||
      assignment.planned_practitioner_id !== (practitioner?.id ?? null) ||
      assignment.planned_exercise_mode_id !== exerciseModeId);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("planning.editAssignment")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          {plannedChanged && assignment?.planned_date && (
            <Alert severity="info">
              {t("planning.plannedWas", {
                date: assignment.planned_date,
                slot:
                  sortedSlots.find((s) => s.id === assignment.planned_slot_template_id)
                    ?.label ?? "?",
                mode:
                  modeById.get(assignment.planned_exercise_mode_id ?? "")?.code ?? "?",
              })}
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t("planning.date")}</InputLabel>
              <Select
                label={t("planning.date")}
                value={date}
                onChange={(e) => setDate(String(e.target.value))}
              >
                {monthDates.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t("planning.slot")}</InputLabel>
              <Select
                label={t("planning.slot")}
                value={slotId}
                onChange={(e) => setSlotId(String(e.target.value))}
              >
                {sortedSlots.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)} · {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Autocomplete
            options={practitioners.data?.items ?? []}
            getOptionLabel={(p) => p.display_name}
            value={practitioner}
            onChange={(_, v) => setPractitioner(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label={t("planning.selectPractitioner")} />
            )}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t("planning.selectExerciseMode")}</InputLabel>
              <Select
                label={t("planning.selectExerciseMode")}
                value={exerciseModeId}
                onChange={(e) => setExerciseModeId(String(e.target.value))}
              >
                {(modes.data ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {m.color && (
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: m.color }}
                        />
                      )}
                      <Typography>{m.code}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.label}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(roles.data ?? []).length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{t("planning.selectFunctionalRole")}</InputLabel>
                <Select
                  label={t("planning.selectFunctionalRole")}
                  value={functionalRoleId}
                  onChange={(e) => setFunctionalRoleId(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>{t("planning.noFunctionalRole")}</em>
                  </MenuItem>
                  {(roles.data ?? []).map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.code} — {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          <Divider textAlign="left">
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {t("planning.realizedSection")}
            </Typography>
          </Divider>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={realizedStatus}
            onChange={(_, v) => v && setRealizedStatus(v as RealizedStatus)}
            sx={{ flexWrap: "wrap" }}
          >
            {REALIZED_STATUSES.map((s) => (
              <ToggleButton key={s} value={s}>
                {t(`planning.realized.${s}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {t(`planning.realizedHelp.${realizedStatus}`)}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              type="datetime-local"
              label={t("planning.actualStart")}
              value={actualStart}
              onChange={(e) => setActualStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="datetime-local"
              label={t("planning.actualEnd")}
              value={actualEnd}
              onChange={(e) => setActualEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            type="number"
            label={t("planning.realizedMinutes")}
            helperText={t("planning.realizedMinutesHelp")}
            value={realizedMinutes}
            onChange={(e) => setRealizedMinutes(e.target.value)}
            inputProps={{ min: 0, max: 1440 }}
          />

          {assignment?.practitioner_summary && (
            <Alert severity="info" icon={false}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                {t("planning.practitionerSummary")}
              </Typography>
              {assignment.practitioner_summary}
            </Alert>
          )}
          <TextField
            label={t("planning.managerNote")}
            value={managerNote}
            onChange={(e) => setManagerNote(e.target.value)}
            multiline
            minRows={1}
            maxRows={4}
          />
          <TextField
            label={t("planning.comment")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={1}
            maxRows={3}
          />
          {assignment && (
            <Stack direction="row" spacing={1}>
              <Chip size="small" label={`${t("planning.source")}: ${assignment.source}`} />
              {assignment.realized_at && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${t("planning.realizedAt")}: ${new Date(
                    assignment.realized_at,
                  ).toLocaleString()}`}
                />
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          disabled={mutation.isPending || !assignment}
          onClick={() => mutation.mutate()}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
